import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, qk, unwrap, type Deployment, type DeploymentStatus, type FailureCategory } from "@/lib/api";
import { formatDate } from "@/lib/format";

/** Deploys that are still moving. */
export const IN_FLIGHT: DeploymentStatus[] = ["queued", "waiting_approval", "building", "pre_deploy", "deploying"];
export const isInFlight = (s: DeploymentStatus | string | undefined) => !!s && (IN_FLIGHT as string[]).includes(s);

/** "Pushed to main", "Rolled back", … one plain phrase per trigger kind. */
export function triggerLabel(d: Pick<Deployment, "trigger" | "source">): string {
  const branch = d.source?.commit?.branch;
  switch (d.trigger.kind) {
    case "git_push":
      return branch ? `Pushed to ${branch}` : "Pushed to GitHub";
    case "manual":
      return "Deployed from the dashboard";
    case "api":
      return "Deployed through the API";
    case "manifest":
      return "Applied guv.yaml";
    case "rollback":
      return "Rolled back";
    case "redeploy":
      return "Redeployed";
    case "variables_change":
      return "Variables changed";
    case "auto_patch":
      return "Security patch";
    case "template":
      return "Created from a template";
    case "pull_request":
      return "Pull request preview";
    default:
      return "Deployed";
  }
}

export const failureLabel: Record<FailureCategory, string> = {
  build_failed: "The build failed",
  dependency_install_failed: "Packages would not install",
  start_command_missing: "We could not tell how to start your app",
  port_mismatch: "Listening on the wrong port",
  healthcheck_failed: "Health check failed",
  crash_loop: "Kept crashing on start",
  out_of_memory: "Ran out of memory",
  missing_secret: "A secret is missing",
  secret_in_bundle: "Secret in the browser bundle",
  pre_deploy_failed: "Pre-deploy command failed",
  image_pull_failed: "Could not fetch the image",
  image_rejected: "The image did not pass security checks",
  timeout: "Took too long",
  quota_exceeded: "Plan limit reached",
  spend_cap_reached: "Spend cap reached",
  platform_error: "A problem on our side",
  unknown: "Something went wrong",
};

/** "Today", "Yesterday", "Monday 20 October" in London time. */
export function dayLabel(iso: string, now = Date.now()): string {
  const key = (t: number) => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London" }).format(t);
  const d = new Date(iso).getTime();
  if (key(d) === key(now)) return "Today";
  if (key(d) === key(now - 86400_000)) return "Yesterday";
  if (now - d < 6 * 86400_000) {
    return new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", weekday: "long", day: "numeric", month: "long" }).format(d);
  }
  return formatDate(d);
}

export function groupByDay<T extends { created_at: string }>(items: T[]): { label: string; items: T[] }[] {
  const out: { label: string; items: T[] }[] = [];
  for (const it of items) {
    const label = dayLabel(it.created_at);
    const last = out.at(-1);
    if (last?.label === label) last.items.push(it);
    else out.push({ label, items: [it] });
  }
  return out;
}

/** Ticks every `ms` while `active`, for live durations ("Installing packages… 42 s"). */
export function useTicker(active: boolean, ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [active, ms]);
  return now;
}

/** Operation polled while it runs (cheaper than a stream for list rows). */
export function useLiveOperation(operationId: string | null | undefined, live: boolean) {
  return useQuery({
    queryKey: qk.operation(operationId ?? ""),
    queryFn: () => unwrap(api.GET("/v1/operations/{operation_id}", { params: { path: { operation_id: operationId! } } })),
    enabled: !!operationId && live,
    refetchInterval: live ? 1500 : false,
  });
}

/** GitHub compare URL between two commits of the same repo, if we can build one. */
export function compareUrl(prev: Deployment | undefined, d: Deployment): string | null {
  const a = prev?.source?.commit?.sha;
  const b = d.source?.commit?.sha;
  const url = d.source?.commit?.url;
  if (!a || !b || !url || a === b) return null;
  const repo = url.match(/^(https:\/\/github\.com\/[^/]+\/[^/]+)/)?.[1];
  return repo ? `${repo}/compare/${a.slice(0, 12)}...${b.slice(0, 12)}` : null;
}

/** The deploy that ran before `d` for the same service and environment. */
export function previousOf(all: Deployment[], d: Deployment): Deployment | undefined {
  return all
    .filter((x) => x.service_id === d.service_id && x.environment_id === d.environment_id && x.created_at < d.created_at && x.status !== "failed" && x.status !== "cancelled")
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
}
